import { BehaviorSubject } from 'rxjs';
import { Injectable } from '@angular/core';
import firebase from 'firebase';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class FilesService {
  public filesArray = new BehaviorSubject([]);
  public selectedFiles = new BehaviorSubject([]);

  cp_selectionChanged(file, isSelected) {
    const selectedFiles = this.selectedFiles.getValue();
    if (isSelected) {
      selectedFiles.push(file);
    } else {
      const index = selectedFiles.findIndex(f => f.id === file.id);
      selectedFiles.splice(index, 1);
    }
    this.selectedFiles.next(selectedFiles);
  }

  async getFilesByCaseName(casename: any, docs: boolean, avoidedFolderId?: string) {
    let docsData;
    if (avoidedFolderId) {
      docsData = (
        await firebase
          .firestore()
          .collection('files')
          .where('belongsTo', '==', casename)
          .where('parentFolder', '!=', avoidedFolderId)
          .get()
      ).docs;
    } else {
      docsData = (await firebase.firestore().collection('files').where('belongsTo', '==', casename).get()).docs;
    }

    return docsData.length ? <firebase.firestore.DocumentData>docsData
          .map(doc => <firebase.firestore.DocumentData>{ ...doc.data(), id: doc.id })
          .map(file => ({
            ...file,
            fdate: file.fdate ? this.formatDate(file.fdate) : '',
            uploadedDate: file.uploadedDate ? this.formatDate(file.uploadedDate) : '',
            lastModified: file.lastModified ? this.formatDate(file.lastModified) : '',
          })) : [];
  }
  removeDownloadLinkFromFile(fileId: string) {
    return firebase
      .firestore()
      .collection('files')
      .where('fileId', '==', fileId)
      .get()
      .then(({ docs }) => {
        docs[0].ref.update({ downloadLink: null });
      });
  }
  
  getFileByDICOMDIRFileId(fileId: string) {
    return firebase.firestore().collection('files').where('DICOMDIRFileId', '==', fileId).limit(1).get();
  }

  deleteDownloadLink(fileId: any) {
    return firebase
      .firestore()
      .collection('files')
      .where('fileId', '==', fileId)
      .get()
      .then(({ docs }) => {
        docs[0].ref.update({ downloadLink: null });
      });
  }

  updateDeletedZippedDIscContentsFile(DICOMDIRFileId: any) {
    return firebase
      .firestore()
      .collection('files')
      .where('DICOMDIRFileId', '==', DICOMDIRFileId)
      .get()
      .then(({ docs }) => {
        // Delete the field DICOMDIRFileId.
        docs.forEach(doc => {
          doc.ref.update({ DICOMDIRFileId: null });
        });
      });
  }

  getParentFolderByFileId(fileId: string) {
    return firebase
      .firestore()
      .collection('files')
      .where('fileId', '==', fileId)
      .limit(1)
      .get()
      .then(({ docs }) => {
        if (docs.length > 0) {
          return docs[0].data().parentFolder;
        } else {
          console.log('No parent folder found for file with id: ', fileId);

          return null;
        }
      });
  }

  getZippedFileByDDICOMDIRFileId(fileId: any) {
    return firebase
      .firestore()
      .collection('files')
      .where('fileId', '==', 'zippedDisc_' + fileId)
      .limit(1)
      .get()
      .then(({ docs }) => {
        return docs[0].data();
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async deleteFolder(folderId: string) {
    const db = firebase.firestore();
    const { filePaths, docIds } = await this.collectFolderContents(folderId);

    if (filePaths.length) {
      await this.deleteFilesFromStorage(filePaths);
    }

    // Delete collected docs + the folder document itself in batches of 500
    const { docs: folderDocs } = await db.collection('files').where('folderId', '==', folderId).get();
    const allDocIds = [...docIds, ...folderDocs.map(d => d.id)];
    const chunks: string[][] = [];
    for (let i = 0; i < allDocIds.length; i += 500) chunks.push(allDocIds.slice(i, i + 500));
    for (const chunk of chunks) {
      const batch = db.batch();
      chunk.forEach(id => batch.delete(db.collection('files').doc(id)));
      await batch.commit();
    }
  }

  private async collectFolderContents(folderId: string): Promise<{ filePaths: string[], docIds: string[] }> {
    const result: { filePaths: string[], docIds: string[] } = { filePaths: [], docIds: [] };
    const { docs } = await firebase.firestore().collection('files').where('parentFolder', '==', folderId).get();
    for (const doc of docs) {
      const data = doc.data();
      result.docIds.push(doc.id);
      if (data.type === 'folder') {
        const sub = await this.collectFolderContents(data.folderId);
        result.filePaths.push(...sub.filePaths);
        result.docIds.push(...sub.docIds);
      } else if (data.filePath) {
        result.filePaths.push(data.filePath);
      }
    }
    return result;
  }

  async checkFolderIsEmpty(folderId: string) {
    const { docs } = await firebase.firestore().collection('files').where('parentFolder', '==', folderId).get();
    return docs.length === 0;
  }

  deleteGoogleStorageFile(filePath: string, storageBucket: string) {
    console.log('filePath: ', filePath);
    console.log('storageBucket: ', storageBucket);
    return firebase.functions().httpsCallable('firebaseUtilities-fileDeleteFromStorageV2')({
      filePaths: [filePath],
      bucket: storageBucket,
    });
  }

  async deleteFileByFileId(fileId: string) {
    const { docs } = await firebase.firestore().collection('files').where('fileId', '==', fileId).get();
    return docs.forEach(doc => doc.ref.delete());
  }

  async getFolderLabel(folderId: string, folderName: string, { isConsultant, useremail, isClient }) {
    let count = 0;
    if (isConsultant || isClient) {
      const filesInThisFolder = (
        await firebase
          .firestore()
          .collection('files')
          .where('parentFolder', '==', folderId)
          .where('ftype', '==', 'File')
          .get()
      ).docs;

      if (filesInThisFolder.length === 0) {
        return folderName;
      }

      filesInThisFolder
        .map(file => file.data())
        .forEach(file => {
          file.sharedUsers?.forEach(({ email }) => {
            if (email.toLowerCase() === useremail.toLowerCase()) {
              count++;
            }
          });
        });
    } else {
      count = (await firebase.firestore().collection('files').where('parentFolder', '==', folderId).get()).size;
    }
    return `${this.findFolderAlias(folderName)} (${count})`;
  }

  findFolderAlias(folderName) {
    const foldersAliases = [
      {
        name: 'Discs',
        alias: 'Zipped files for discs creation',
      },
    ];
    return foldersAliases.find(f => f.name === folderName)?.alias || folderName;
  }

  getFileUrl(filePath: string) {
    return firebase.functions().httpsCallable('firebaseUtilities-fileGetSignedURL')({ filePath });
  }

  getDicomUploadSignedUrl(
    filePath: string,
    contentType: string = 'application/octet-stream',
    bucket: string = environment.config.storageBucket,
  ): Promise<{ signedUrl: string; gsUri: string }> {
    return firebase
      .functions()
      .httpsCallable('firebaseUtilities-getDicomUploadSignedURLV1')({ filePath, contentType, bucket })
      .then(result => result.data as { signedUrl: string; gsUri: string });
  }

  submitForProcessing(
    dicomStagingUploadIds: string[],
    imagingCaseId: string,
  ): Promise<{ submitted: number }> {
    return firebase
      .functions()
      .httpsCallable('submitForProcessingV1')({ dicomStagingUploadIds, imagingCaseId })
      .then(result => result.data as { submitted: number });
  }

  linkToClioMatter(
    imagingCaseId: string,
    clioMatterId: number,
    clioMatterName: string,
    clioMatterRef: string,
  ): Promise<{ patientCaseName: string }> {
    return firebase
      .functions()
      .httpsCallable('linkToClioMatterV1')({
        imagingCaseId, clioMatterId, clioMatterName, clioMatterRef,
      })
      .then(result => result.data as { patientCaseName: string });
  }

  async getFilesByParentFolderId(
    folderId: string,
    casename: string,
    authoredBy?: string,
    roleConsultant = false,
    email: string | null = null,
  ) {
    interface FileData extends firebase.firestore.DocumentData {
      id: string;
      type: string;
      parentFolder?: string;
      parentFolders?: Array<{ folderId: string }>;
      fdate?: string;
      name?: string;
      uploadedDate?: string;
      lastModified?: string;
      sharedUsers?: Array<{ email: string }>;
    }

    // const isFile = (file: FileData): boolean => file.type !== 'folder';

    const isInFolder = (file: FileData): boolean => {
      if (file.parentFolder === folderId) {
        return true;
      }
      return !!file.parentFolders?.some(folder => folder.folderId === folderId);
    };

    const formatFileDates = (file: FileData): FileData => ({
      ...file,
      fdate: file.fdate ? this.formatDate(file.fdate) : '',
      uploadedDate: file.uploadedDate ? this.formatDate(file.uploadedDate) : '',
      lastModified: file.lastModified ? this.formatDate(file.lastModified) : '',
    });

    const query = firebase.firestore().collection('files').where('belongsTo', '==', casename);
    const finalQuery = authoredBy ? query.where('creator', '==', authoredBy) : query;
    const data = await finalQuery.get();
    const mappedData = doc => ({ ...doc.data(), id: doc.id }) as FileData;

    const files = data.docs.map(doc => mappedData(doc));

    let resultFiles = files.filter(isInFolder).map(formatFileDates);

    if (roleConsultant && email) {
      resultFiles = resultFiles.filter(file =>
        file.sharedUsers?.some(user => user.email.toLowerCase() === email.toLowerCase()),
      );
    }

    return resultFiles;
  }

  formatDate(dateString) {
    return new Date(dateString).toLocaleDateString('en-EN');
  }

  getOtherFolderId(casename: string) {
    return firebase
      .firestore()
      .collection('files')
      .where('belongsTo', '==', casename)
      .where('type', '==', 'folder')
      .where('name', '==', 'Other')
      .get()
      .then(({ docs }) => docs[0].data().folderId);
  }

  public addCurrentPatientFile(file) {
    const newArr = this.filesArray.getValue().push(file);
    this.filesArray.next([newArr]);
  }

  public addPatientFiles(filesArr) {
    this.filesArray.next(filesArr);
  }

  public getFilesArray() {
    return this.filesArray.getValue();
  }

  updateFileByDocId(docId: string, data: any) {
    return firebase.firestore().collection('files').doc(docId).update(data);
  }

  getFileByFileId(fileId: string) {
    return firebase.firestore().collection('files').where('fileId', '==', fileId).limit(1).get();
  }

  getFilesByFileIdsArr(fileIdsArr: string[]) {
    return firebase.firestore().collection('files').where('fileId', 'in', fileIdsArr).get();
  }

  getFilesByFolderId(folderId: string) {
    return firebase.firestore().collection('files').where('folderId', '==', folderId).get();
  }

  async deleteFileByDocId(file) {
    await firebase.firestore().collection('files').doc(file.id).delete();
    return { fileId: file.fileId, docId: file.id };
  }

  private async ungroupFilesIds(filesIds: string[]) {
    const chunkSize = 10;
    const groups = [];

    for (let g = 0; g < filesIds.length; g++) {
      const group: string[] = [];

      for (let h = 0; h < chunkSize; h++) {
        if (!filesIds[h + g]) {
          continue;
        }

        group.push(filesIds[h + g]);
      }
      groups.push(group);
      g += chunkSize;
    }
    return groups;
  }

  async getParentFolders(filesIDs: string[]): Promise<string[]> {
    if (filesIDs.length > 10) {
      const groups: any[] = await this.ungroupFilesIds(filesIDs);
      let arr: string[] = [];

      for (var h = 0; h < groups.length; h++) {
        const folderIDs = await this.getParentFolderIDsByFileIDs(groups[h]);
        arr = [...arr, ...folderIDs];
      }

      return arr;
    } else {
      return this.getParentFolderIDsByFileIDs(filesIDs);
    }
  }

  private async getParentFolderIDsByFileIDs(fileIds: string[]): Promise<string[]> {
    return firebase
      .firestore()
      .collection('files')
      .where('fileId', 'in', fileIds)
      .get()
      .then(qS => {
        const folderIDs: string[] = qS.docs.map(doc => <string>doc.data().parentFolder);
        return folderIDs;
      });
  }

  async getFilesCountByClient(casename: string, roleConsultant, consultantEmail: string) {
    const { docs } = await firebase.firestore().collection('files').where('belongsTo', '==', casename).get();
    const items = docs.map(item => item.data()).filter(({ type }) => type !== 'folder');
    if (!roleConsultant) {
      return items.length;
    } else {
      return items.filter(
        ({ sharedUsers }) =>
          sharedUsers && sharedUsers.map(({ email }) => email.toLowerCase()).includes(consultantEmail.toLowerCase()),
      ).length;
    }
  }

  updateFileField(fileId: string, field: any) {
    firebase
      .firestore()
      .collection('files')
      .where('fileId', '==', fileId)
      .limit(1)
      .get()
      .then(({ docs }) => {
        docs[0].ref.update(field);
      });
  }

  deleteFilesFromStorage(filePaths: string[]) {
    const bucket = environment.config.storageBucket;
    return firebase.functions().httpsCallable('firebaseUtilities-fileDeleteFromStorageV2')({ bucket, filePaths });
    // return firebase.functions().httpsCallable('firebaseUtilities-fileDeleteFromStorage')({ bucket, filePaths });
  }
}
